import React, {
  useCallback, useEffect, useMemo, useState, ReactElement,
} from 'react';
import Card from '../../../components/card/card.component';
import Button from '../../../components/button/button.component';
import ModalButtonBlock from '../../../components/modal-button-block/modal-button-block.component';
import ErrorBlock from '../../../components/error-block/error-block.component';
import ModalLabel from '../../../components/modal-label/modal-label.component';
import ModalLayout from '../../../components/modal-layout/modal-layout.component';
import ModalFieldBlock from '../../../components/modal-field-block/modal-field-block.component';
import Loader from '../../../components/loader/loader.component';
import FieldSelect from '../../../components/field-select/field-select.component';
import FieldDate from '../../../components/field-date/field-date.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import useScreenSmall from '../../../hooks/use-screen-small';
import { FILTER_VALUE_EMPTY } from '../../../constants/system/filter-values';
import PersonDefaultImage from '../../../static/images/person-default-image.png';

const DealTaskListModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isOpen,
    isSaving,
    errorMessage,
    contactList,
    onChange,
    onSaveForm,
    onCloseModal,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();

  const [dueDateValue, setDueDateValue]: [string | null, Function] = useState(null);
  const [contactIdValue, setContactIdValue]: [number | null, Function] = useState(null);
  const [dueDateError, setDueDateError]: [string | null, Function] = useState(null);

  const isSaveDisabled: boolean = (!dueDateValue && !contactIdValue && contactIdValue !== FILTER_VALUE_EMPTY)
    || !!dueDateError;
  const defaultContactItem: Contact = {
    id: FILTER_VALUE_EMPTY,
    fullName: 'Unassigned',
    photo: PersonDefaultImage,
    photoThumbnail: PersonDefaultImage,
  };
  const extendedContactList: Contact[] = contactList ? [defaultContactItem, ...contactList] : [defaultContactItem];

  const menuItemContactList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionItemExtraLabelKey: 'email',
    optionList: extendedContactList,
    defaultImage: PersonDefaultImage,
  }), [extendedContactList]);

  const closeModal: Function = useCallback((): void => {
    if (onCloseModal) {
      onCloseModal();
    }

    setContactIdValue('');
    setDueDateValue(null);
  }, []);

  const onDueDateChange: Function = useCallback((dueDate: Date): void => {
    setDueDateValue(dueDate);

    if (onChange) {
      onChange();
    }
  }, []);

  const onContactChange: Function = useCallback((contactId: number): void => {
    setContactIdValue(contactId);

    if (onChange) {
      onChange();
    }
  }, []);

  const onDueDateError: Function = useCallback((error?: string): void => {
    setDueDateError(error || null);
  }, []);

  const onSaveFormClick: MouseEventHandlerFunction = useCallback((): void => {
    if ((!contactIdValue && contactIdValue !== FILTER_VALUE_EMPTY && !dueDateValue) || dueDateError) {
      return;
    }

    if (onSaveForm) {
      onSaveForm(contactIdValue, dueDateValue, (): void => {
        setContactIdValue('');
        setDueDateValue(null);
      });
    }
  }, [contactIdValue, dueDateValue, dueDateError]);

  useEffect((): void => {
    setContactIdValue('');
    setDueDateValue(null);
  }, []);

  return (
    <ModalLayout isOpen={isOpen} title="Update Tasks" onCloseModal={closeModal}>
      <Card isContainer>
        <ModalLabel>
          Select new Assigned To:
        </ModalLabel>
        <ModalFieldBlock>
          <FieldSelect
            isPlain
            isBordered
            isDisabled={contactList && !contactList.length}
            value={contactIdValue}
            onChange={(event: EventSelectChange, value: string): void => {
              onContactChange(value);
            }}
          >
            {menuItemContactList}
          </FieldSelect>
        </ModalFieldBlock>
        <ModalLabel>
          Select new Due Date:
        </ModalLabel>
        <ModalFieldBlock>
          <FieldDate
            isPlain
            isBordered
            value={dueDateValue}
            onChange={onDueDateChange}
            onError={onDueDateError}
          />
        </ModalFieldBlock>
      </Card>
      <ModalButtonBlock>
        <Button isActive isDisabled={isSaveDisabled} onClick={onSaveFormClick}>
          Save
        </Button>
        {
          isSaving && !isSmallScreen && (
            <Loader isInline isModal isSmall />
          )
        }
      </ModalButtonBlock>
      {
        !!errorMessage && (
          <ErrorBlock errorMessage={errorMessage} />
        )
      }
    </ModalLayout>
  );
});

export default DealTaskListModal;
